/**
 * Shared Top 100 Leaderboard & Hall of Fame Component for Scored Tools
 * Standalone, modern, mobile-responsive, paginated at exactly 20 rows per page.
 * Features:
 * - Live server synchronization with /api/leaderboard
 * - Top 100 qualification detection
 * - Name prompt modal with server-side anomaly & profanity validation
 * - 20 items per page pagination (1-5 pages)
 * - Gold/Silver/Bronze podium styling and country flags
 */

import { setTextSafe } from './utils.js';
import { showToast } from './modal.js';

function countryToFlag(code) {
  if (!code || code === 'XX' || code.length !== 2) return '🌐';
  const c = code.toUpperCase();
  const first = 0x1F1E6 + (c.charCodeAt(0) - 65);
  const second = 0x1F1E6 + (c.charCodeAt(1) - 65);
  return String.fromCodePoint(first, second);
}

function formatRelativeTime(dateStr, isAr) {
  if (!dateStr) return isAr ? 'مؤخراً' : 'Recently';
  const time = new Date(dateStr).getTime();
  const diffSec = Math.floor((Date.now() - time) / 1000);
  if (isNaN(diffSec) || diffSec < 60) return isAr ? 'الآن' : 'Just now';
  if (diffSec < 3600) {
    const mins = Math.floor(diffSec / 60);
    return isAr ? `منذ ${mins} دقيقة` : `${mins}m ago`;
  }
  if (diffSec < 86400) {
    const hours = Math.floor(diffSec / 3600);
    return isAr ? `منذ ${hours} ساعة` : `${hours}h ago`;
  }
  const days = Math.floor(diffSec / 86400);
  if (days === 1) return isAr ? 'أمس' : 'Yesterday';
  if (days < 30) return isAr ? `منذ ${days} يوماً` : `${days}d ago`;
  return new Date(dateStr).toLocaleDateString(isAr ? 'ar-EG' : 'en-US', { month: 'short', day: 'numeric' });
}

export class LeaderboardManager {
  constructor(options = {}) {
    this.toolId = options.toolId;
    this.metricLabel = options.metricLabel || 'Score';
    this.secondaryLabel = options.secondaryLabel || 'الدقة';
    this.isHigherBetter = options.isHigherBetter !== false;
    this.isAr = document.documentElement.lang === 'ar' || document.documentElement.dir === 'rtl';

    this.scores = [];
    this.totalRecords = 0;
    this.threshold = null;
    this.currentPage = 1;
    this.pageSize = 20; // Strictly 20 per page as required
    this.pendingSubmission = null;
    this.highlightId = null;

    this.init();
  }

  init() {
    this.mountDOM();
    this.bindEvents();
    this.fetchScores();
  }

  mountDOM() {
    // Only mount once per page
    if (document.getElementById('kz-leaderboard-panel')) return;

    const workspace = document.querySelector('.tool-workspace');
    if (!workspace) return;

    const panelHtml = `
      <section class="kz-leaderboard-panel" id="kz-leaderboard-panel" aria-label="${this.isAr ? 'لوحة المتصدرين' : 'Top 100 Leaderboard'}">
        <header class="kz-lb-header">
          <div class="kz-lb-title-wrap">
            <div class="kz-lb-trophy-badge" aria-hidden="true">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/><path d="M4 22h16"/><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/></svg>
            </div>
            <div>
              <h2 class="kz-lb-title">${this.isAr ? 'أعلى 100 نتيجة محققة عالمياً (Top 100)' : 'Top 100 Global Hall of Fame'}</h2>
              <p class="kz-lb-subtitle">${this.isAr ? 'قائمة المتصدرين المعتمدة والمفحوصة سيرفرياً ضد التلاعب والأرقام غير المنطقية' : 'Verified global high scores validated against anomalies and tampering'}</p>
            </div>
          </div>
          <div class="kz-lb-actions">
            <div class="kz-lb-live-indicator">
              <span class="kz-lb-pulse-dot"></span>
              <span>${this.isAr ? 'مباشر' : 'Live'}</span>
            </div>
          </div>
        </header>

        <!-- Leaderboard Table Container -->
        <div class="kz-lb-table-wrap">
          <table class="kz-lb-table" aria-label="${this.isAr ? 'جدول أعلى 100 نتيجة' : 'Leaderboard Table'}">
            <thead>
              <tr>
                <th scope="col" class="kz-col-rank">${this.isAr ? 'الترتيب' : 'Rank'}</th>
                <th scope="col" class="kz-col-player">${this.isAr ? 'اللاعب' : 'Player'}</th>
                <th scope="col" class="kz-col-score">${this.isAr ? 'النتيجة' : 'Score'}</th>
                <th scope="col" class="kz-col-metric">${this.isAr ? 'الدقة / الإحصاء' : 'Metric / Accuracy'}</th>
                <th scope="col" class="kz-col-date">${this.isAr ? 'التاريخ' : 'Date'}</th>
              </tr>
            </thead>
            <tbody id="kz-lb-tbody">
              <tr>
                <td colspan="5" class="kz-lb-loading-cell">
                  <div class="kz-lb-spinner"></div>
                  <span>${this.isAr ? 'جارٍ تحميل قائمة أفضل 100 نتيجة...' : 'Loading verified scores...'}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- 20-per-page Pagination Footer -->
        <nav class="kz-lb-pagination" aria-label="${this.isAr ? 'صفحات المتصدرين' : 'Leaderboard Pagination'}">
          <div class="kz-lb-page-info" id="kz-lb-page-info">
            ${this.isAr ? 'عرض 0 من 0' : 'Showing 0 of 0'}
          </div>
          <div class="kz-lb-page-nav">
            <button class="kz-lb-page-btn" id="kz-lb-prev-btn" type="button" disabled aria-label="${this.isAr ? 'الصفحة السابقة' : 'Previous Page'}">
              ${this.isAr ? 'السابق' : 'Prev'}
            </button>
            <div class="kz-lb-pages-list" id="kz-lb-pages-list"></div>
            <button class="kz-lb-page-btn" id="kz-lb-next-btn" type="button" disabled aria-label="${this.isAr ? 'الصفحة التالية' : 'Next Page'}">
              ${this.isAr ? 'التالي' : 'Next'}
            </button>
          </div>
        </nav>
      </section>

      <!-- Modal Dialog: Record Qualified Score -->
      <dialog id="kz-record-score-dialog" class="kz-dialog kz-record-dialog" aria-labelledby="kz-rec-title">
        <div class="kz-dialog-card kz-record-card">
          <header class="kz-dialog-header">
            <div class="kz-dialog-header-inner">
              <span class="kz-rec-trophy" aria-hidden="true">🏆</span>
              <div>
                <h3 id="kz-rec-title" class="kz-dialog-title">${this.isAr ? 'تهانينا! نتيجتك تأهلت لأفضل 100' : 'Congratulations! Top 100 Qualified'}</h3>
                <p class="kz-dialog-desc" id="kz-rec-subtitle">${this.isAr ? 'حققت أداءً استثنائياً يؤهلك للوحة الشرف العالمية' : 'Outstanding performance! Record your name in the global hall of fame.'}</p>
              </div>
            </div>
            <button type="button" class="kz-dialog-close-btn" id="kz-rec-close" aria-label="${this.isAr ? 'إغلاق' : 'Close'}">✕</button>
          </header>

          <div class="kz-rec-score-box">
            <div class="kz-rec-score-left">
              <span class="kz-rec-score-num" id="kz-rec-score-val">0</span>
              <span class="kz-rec-score-unit" id="kz-rec-score-unit">${this.metricLabel}</span>
            </div>
            <span class="kz-rec-proj-badge" id="kz-rec-proj-badge">${this.isAr ? 'المركز المتوقع: #1' : 'Projected Rank: #1'}</span>
          </div>

          <form id="kz-record-form" class="kz-record-form" novalidate>
            <div class="kz-rec-form-group">
              <label for="kz-player-name-input" class="kz-rec-label">
                ${this.isAr ? 'اسمك أو لقبك المستعار في لوحة الشرف:' : 'Your Player Handle / Nickname:'}
              </label>
              <input
                type="text"
                id="kz-player-name-input"
                class="kz-rec-input"
                placeholder="${this.isAr ? 'مثال: صقر_التحدي أو Falcon99' : 'e.g., CyberFlick or Ghost99'}"
                maxlength="24"
                minlength="2"
                required
                autocomplete="nickname"
              />
              <span class="kz-rec-input-hint">${this.isAr ? 'بين 2 إلى 24 حرفاً' : 'Between 2 and 24 characters'}</span>
            </div>

            <!-- Error Banner: Shown ONLY when an error or inappropriate word is detected -->
            <div id="kz-rec-error" class="kz-rec-error" style="display: none;" role="alert"></div>

            <div class="kz-rec-actions">
              <button type="button" id="kz-rec-skip-btn" class="kz-btn-rec-cancel">
                ${this.isAr ? 'تخطي' : 'Skip'}
              </button>
              <button type="submit" id="kz-rec-submit-btn" class="kz-btn-rec-submit">
                <span>${this.isAr ? 'تأكيد وتسجيل النتيجة' : 'Submit Score to Top 100'}</span>
              </button>
            </div>
          </form>
        </div>
      </dialog>
    `;

    workspace.insertAdjacentHTML('afterend', panelHtml);
  }

  bindEvents() {
    document.getElementById('kz-lb-prev-btn')?.addEventListener('click', () => {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.renderTable();
      }
    });

    document.getElementById('kz-lb-next-btn')?.addEventListener('click', () => {
      const maxPages = Math.max(1, Math.ceil(this.scores.length / this.pageSize));
      if (this.currentPage < maxPages) {
        this.currentPage++;
        this.renderTable();
      }
    });

    // Record Score Form Submission
    const form = document.getElementById('kz-record-form');
    const dialog = document.getElementById('kz-record-score-dialog');
    const closeBtn = document.getElementById('kz-rec-close');
    const skipBtn = document.getElementById('kz-rec-skip-btn');

    closeBtn?.addEventListener('click', () => dialog?.close());
    skipBtn?.addEventListener('click', () => dialog?.close());

    form?.addEventListener('submit', async (e) => {
      e.preventDefault();
      await this.submitScore();
    });
  }

  async fetchScores(isManual = false) {
    const refreshBtn = document.getElementById('kz-lb-refresh-btn');
    if (refreshBtn) refreshBtn.classList.add('spinning');

    try {
      const res = await fetch(`/api/leaderboard?tool=${encodeURIComponent(this.toolId)}&limit=100&_t=${Date.now()}`, {
        cache: 'no-store'
      });
      if (res.ok) {
        const data = await res.json();
        this.scores = data.leaderboard || [];
        this.totalRecords = data.totalRecords || this.scores.length;
        this.threshold = data.threshold;
        this.renderTable();
      } else {
        this.renderError();
      }
    } catch {
      this.renderError();
    } finally {
      if (refreshBtn) {
        setTimeout(() => refreshBtn.classList.remove('spinning'), 400);
      }
    }
  }

  renderTable() {
    const tbody = document.getElementById('kz-lb-tbody');
    if (!tbody) return;

    if (this.scores.length === 0) {
      tbody.innerHTML = `
        <tr>
          <td colspan="5" class="kz-lb-empty-cell">
            <div class="kz-lb-empty-icon" aria-hidden="true">🏆</div>
            <h4>${this.isAr ? 'لا توجد نتائج مسجلة حتى الآن' : 'No High Scores Yet'}</h4>
            <p>${this.isAr ? 'أكمل التمرين لتكون أول من ينقش اسمه في لوحة الشرف الرسمية!' : 'Complete the test to claim the #1 spot in the Hall of Fame!'}</p>
          </td>
        </tr>
      `;
      this.renderPagination();
      return;
    }

    const startIndex = (this.currentPage - 1) * this.pageSize;
    const pageRows = this.scores.slice(startIndex, startIndex + this.pageSize);

    let html = '';
    for (let i = 0; i < pageRows.length; i++) {
      const row = pageRows[i];
      const rankNum = startIndex + i + 1;
      let rankBadge = `#${rankNum}`;
      let rowClass = 'kz-lb-row';

      if (rankNum === 1) {
        rankBadge = '🥇 #1';
        rowClass += ' rank-gold';
      } else if (rankNum === 2) {
        rankBadge = '🥈 #2';
        rowClass += ' rank-silver';
      } else if (rankNum === 3) {
        rankBadge = '🥉 #3';
        rowClass += ' rank-bronze';
      }

      if (this.highlightId && row.id === this.highlightId) {
        rowClass += ' kz-row-highlighted';
      }

      const flag = countryToFlag(row.country);
      const devIcon = row.device === 'mobile' ? '📱' : (row.device === 'tablet' ? '📟' : '💻');
      const timeRel = formatRelativeTime(row.createdAt, this.isAr);

      let metricFormatted = '';
      if (typeof row.secondaryMetric === 'number' && row.secondaryMetric > 0) {
        metricFormatted = `${row.secondaryMetric}%`;
      } else {
        metricFormatted = '--';
      }

      html += `
        <tr class="${rowClass}">
          <td class="kz-col-rank"><span class="kz-rank-pill">${rankBadge}</span></td>
          <td class="kz-col-player">
            <span class="kz-player-name" title="${escapeHtml(row.playerName)}">${escapeHtml(row.playerName)}</span>
            <span class="kz-player-meta" title="${row.country}">
              <span class="kz-flag" aria-hidden="true">${flag}</span>
              <span class="kz-device" aria-hidden="true">${devIcon}</span>
            </span>
          </td>
          <td class="kz-col-score">
            <span class="kz-score-val">${typeof row.score === 'number' ? row.score.toLocaleString() : row.score}</span>
            <span class="kz-score-unit">${row.metricLabel || this.metricLabel}</span>
          </td>
          <td class="kz-col-metric">
            <span class="kz-metric-pill">${metricFormatted}</span>
          </td>
          <td class="kz-col-date">
            <time datetime="${row.createdAt}">${timeRel}</time>
          </td>
        </tr>
      `;
    }

    tbody.innerHTML = html;
    this.renderPagination();
  }

  renderPagination() {
    const pageInfo = document.getElementById('kz-lb-page-info');
    const prevBtn = document.getElementById('kz-lb-prev-btn');
    const nextBtn = document.getElementById('kz-lb-next-btn');
    const pagesList = document.getElementById('kz-lb-pages-list');

    const totalCount = this.scores.length;
    const maxPages = Math.max(1, Math.ceil(totalCount / this.pageSize));

    if (this.currentPage > maxPages) this.currentPage = maxPages;

    const startItem = totalCount === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1;
    const endItem = Math.min(this.currentPage * this.pageSize, totalCount);

    if (pageInfo) {
      pageInfo.textContent = this.isAr
        ? `عرض ${startItem} - ${endItem} من ${totalCount} متصدر`
        : `Showing ${startItem} - ${endItem} of ${totalCount} entries`;
    }

    if (prevBtn) prevBtn.disabled = this.currentPage <= 1;
    if (nextBtn) nextBtn.disabled = this.currentPage >= maxPages;

    if (pagesList) {
      let pagesHtml = '';
      for (let p = 1; p <= maxPages; p++) {
        const active = p === this.currentPage ? 'active' : '';
        pagesHtml += `
          <button type="button" class="kz-lb-page-num ${active}" data-page="${p}" aria-label="${this.isAr ? 'الصفحة ' + p : 'Page ' + p}">
            ${p}
          </button>
        `;
      }
      pagesList.innerHTML = pagesHtml;

      pagesList.querySelectorAll('.kz-lb-page-num').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          const p = Number(e.currentTarget.dataset.page);
          if (p && p !== this.currentPage) {
            this.currentPage = p;
            this.renderTable();
          }
        });
      });
    }
  }

  renderError() {
    const tbody = document.getElementById('kz-lb-tbody');
    if (!tbody) return;
    tbody.innerHTML = `
      <tr>
        <td colspan="5" class="kz-lb-empty-cell">
          <p>${this.isAr ? 'تعذر تحميل قائمة المتصدرين حالياً. يرجى المحاولة لاحقاً.' : 'Could not load leaderboard scores. Please try again.'}</p>
        </td>
      </tr>
    `;
  }

  /**
   * Checks if a score qualifies for Top 100, and if so, shows the registration prompt modal.
   */
  /**
   * Checks if a score qualifies for Top 100, and if so, shows the registration prompt modal
   * or auto-updates if the player has previously registered their name.
   */
  checkAndPromptScore({ score, secondaryMetric = 0, telemetry = {} }) {
    const numScore = parseFloat(score);
    if (isNaN(numScore) || numScore <= 0) return false;

    // Determine qualification
    let qualifies = false;
    let projectedRank = 1;

    if (this.scores.length < 100) {
      qualifies = true;
    } else {
      const lastScore = this.scores[99].score;
      qualifies = this.isHigherBetter ? (numScore > lastScore) : (numScore < lastScore);
    }

    if (!qualifies) return false;

    // Calculate projected rank
    if (this.isHigherBetter) {
      const rankIdx = this.scores.findIndex((s) => numScore > s.score);
      projectedRank = rankIdx === -1 ? this.scores.length + 1 : rankIdx + 1;
    } else {
      const rankIdx = this.scores.findIndex((s) => numScore < s.score);
      projectedRank = rankIdx === -1 ? this.scores.length + 1 : rankIdx + 1;
    }

    this.pendingSubmission = {
      score: numScore,
      secondaryMetric: parseFloat(secondaryMetric) || 0,
      telemetry,
      projectedRank
    };

    // Check if player has already entered their name before
    let savedName = null;
    try {
      savedName = localStorage.getItem('kz_player_name');
    } catch {}

    if (savedName && savedName.trim().length >= 2) {
      // 2nd time or returning user: DO NOT prompt or ask for name again.
      // Auto-submit in background and update score if higher!
      this.submitScore(savedName.trim());
      return true;
    }

    // First time user: Prompt for nickname in modal
    this.showPromptModal(numScore, projectedRank);
    return true;
  }

  showPromptModal(score, projectedRank) {
    const dialog = document.getElementById('kz-record-score-dialog');
    const scoreVal = document.getElementById('kz-rec-score-val');
    const projBadge = document.getElementById('kz-rec-proj-badge');
    const errorBanner = document.getElementById('kz-rec-error');
    const nameInput = document.getElementById('kz-player-name-input');

    if (!dialog) return;

    if (scoreVal) scoreVal.textContent = typeof score === 'number' ? score.toLocaleString() : score;
    if (projBadge) projBadge.textContent = this.isAr ? `المركز المتوقع: #${projectedRank}` : `Projected Rank: #${projectedRank}`;
    if (errorBanner) errorBanner.style.display = 'none';

    // Auto-fill saved nickname from localStorage if available
    try {
      const savedName = localStorage.getItem('kz_player_name');
      if (savedName && nameInput) nameInput.value = savedName;
    } catch {}

    dialog.showModal();
    if (nameInput) setTimeout(() => nameInput.focus(), 100);
  }

  async submitScore(playerNameOverride = null) {
    if (!this.pendingSubmission) return;

    const isAutoSubmit = Boolean(playerNameOverride);
    const nameInput = document.getElementById('kz-player-name-input');
    const errorBanner = document.getElementById('kz-rec-error');
    const submitBtn = document.getElementById('kz-rec-submit-btn');
    const dialog = document.getElementById('kz-record-score-dialog');

    const name = (playerNameOverride || nameInput?.value)?.trim();
    if (!name || name.length < 2) {
      if (!isAutoSubmit) {
        this.showModalError(this.isAr ? 'يرجى إدخال اسم مكون من حرفين على الأقل.' : 'Please enter a name with at least 2 characters.');
      }
      return;
    }

    if (submitBtn && !isAutoSubmit) {
      submitBtn.disabled = true;
      submitBtn.classList.add('loading');
    }
    if (errorBanner) errorBanner.style.display = 'none';

    try {
      const payload = {
        tool: this.toolId,
        playerName: name,
        score: this.pendingSubmission.score,
        secondaryMetric: this.pendingSubmission.secondaryMetric,
        telemetry: this.pendingSubmission.telemetry,
        tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
        lang: navigator.language
      };

      const res = await fetch('/api/leaderboard', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await res.json();

      // 1. Successful insertion or update
      if ((res.status === 201 || res.status === 200) && data.success) {
        if (data.keptBest) {
          // Score was not higher than user's existing record
          if (dialog?.open) dialog.close();
          if (isAutoSubmit) {
            showToast(this.isAr 
              ? `تم الاحتفاظ بأفضل نتيجة شخصية مسجلة لك (${data.existingScore})`
              : `Your personal best (${data.existingScore}) is still higher and was kept.`, 
              { type: 'info' }
            );
          }
          return;
        }

        if (data.entry) {
          // Save clean name in localStorage for subsequent auto-submits
          try {
            localStorage.setItem('kz_player_name', data.entry.playerName || name);
          } catch {}

          // Immediately reflect in local state without waiting for refresh!
          const newEntry = data.entry;
          const existingIdx = this.scores.findIndex(
            (s) => s.id === newEntry.id || s.playerName.toLowerCase() === newEntry.playerName.toLowerCase()
          );

          if (existingIdx !== -1) {
            this.scores[existingIdx] = { ...this.scores[existingIdx], ...newEntry };
          } else {
            this.scores.push(newEntry);
          }

          // Sort scores in memory
          this.scores.sort((a, b) => {
            return this.isHigherBetter ? (b.score - a.score) : (a.score - b.score);
          });

          // Strictly keep top 100
          this.scores = this.scores.slice(0, 100);

          // Update ranks
          this.scores.forEach((s, idx) => {
            s.rank = idx + 1;
          });

          this.totalRecords = this.scores.length;
          this.highlightId = newEntry.id;

          const calculatedRank = this.scores.findIndex(s => s.id === newEntry.id) + 1;
          const displayRank = data.rank || calculatedRank || 1;

          // Navigate to page containing user's entry
          this.currentPage = Math.ceil(displayRank / this.pageSize);

          // Render updated table immediately in place (0ms latency, no refresh required!)
          this.renderTable();

          if (dialog?.open) dialog.close();

          if (isAutoSubmit) {
            showToast(this.isAr 
              ? `تهانينا! تم تحسين نتيجتك في قائمة أفضل 100 إلى المركز #${displayRank}`
              : `Congratulations! Your score improved to rank #${displayRank} in the Top 100!`,
              { type: 'success' }
            );
          }

          // Smooth scroll to leaderboard
          const panel = document.getElementById('kz-leaderboard-panel');
          if (panel) {
            panel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }

          // Fetch fresh server state in background
          this.fetchScores();
        }
      } else if (res.status === 409) {
        // Name already taken by another player in the Top 100
        const conflictMsg = data.message_ar || (this.isAr 
          ? 'هذا الاسم مستخدم بالفعل في قائمة أفضل 100 لهذا الاختبار. يرجى اختيار اسم فريد.' 
          : 'This player nickname is already taken in the Top 100. Please choose a unique name.');

        if (isAutoSubmit) {
          // If auto-submit conflicted, open the dialog so the user can choose a new name
          this.showPromptModal(this.pendingSubmission.score, this.pendingSubmission.projectedRank);
        }
        this.showModalError(conflictMsg);
      } else if (res.status === 422) {
        // Profanity or inappropriate name error
        const profMsg = data.message_ar || (this.isAr 
          ? 'الاسم يحتوي على مفردات غير ملائمة، يرجى اختيار اسم نظيف.' 
          : 'The name contains inappropriate language. Please choose a clean name.');
        if (isAutoSubmit) {
          this.showPromptModal(this.pendingSubmission.score, this.pendingSubmission.projectedRank);
        }
        this.showModalError(profMsg);
      } else if (res.status === 400) {
        // Anomaly detected
        const anomMsg = data.message_ar || (this.isAr 
          ? 'تم رصد عدم توافق في النتيجة المسجلة مع المعايير البشرية.' 
          : 'Score failed integrity verification.');
        if (isAutoSubmit) {
          showToast(anomMsg, { type: 'warning' });
        } else {
          this.showModalError(anomMsg);
        }
      } else if (res.status === 429) {
        // Rate limited
        const rateMsg = data.message_ar || (this.isAr 
          ? 'يرجى الانتظار بضع ثوانٍ قبل المحاولة مجدداً.' 
          : 'Please wait a few seconds before trying again.');
        if (isAutoSubmit) {
          showToast(rateMsg, { type: 'warning' });
        } else {
          this.showModalError(rateMsg);
        }
      } else {
        const genMsg = data.message_ar || (this.isAr 
          ? 'حدث خطأ أثناء حفظ النتيجة، يرجى المحاولة لاحقاً.' 
          : 'Could not submit score. Please try again.');
        if (isAutoSubmit) {
          showToast(genMsg, { type: 'error' });
        } else {
          this.showModalError(genMsg);
        }
      }
    } catch {
      const netMsg = this.isAr 
        ? 'تعذر الاتصال بالخادم، يرجى التحقق من اتصالك بالإنترنت.' 
        : 'Network error. Please check your connection.';
      if (isAutoSubmit) {
        showToast(netMsg, { type: 'error' });
      } else {
        this.showModalError(netMsg);
      }
    } finally {
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.classList.remove('loading');
      }
    }
  }

  showModalError(msg) {
    const errorBanner = document.getElementById('kz-rec-error');
    if (errorBanner) {
      errorBanner.textContent = msg;
      errorBanner.style.display = 'block';
    }
  }
}

function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, (char) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  })[char]);
}

let activeLeaderboard = null;

/**
 * Convenience helper to instantiate and mount a leaderboard for any tool
 */
export function mountLeaderboard(options) {
  activeLeaderboard = new LeaderboardManager(options);
  return activeLeaderboard;
}

/**
 * Convenience helper to check if a score qualifies and prompt the user
 */
export function checkAndPromptScore(data) {
  if (activeLeaderboard) {
    return activeLeaderboard.checkAndPromptScore(data);
  }
  return false;
}
